import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useAdmin } from './AdminContext';
import { workspaceService, WORKSPACE_ROLE_PERMISSIONS } from '../services/workspaceService';
import {
  Workspace,
  WorkspaceMember,
  CreateWorkspaceData,
  UpdateWorkspaceData,
  InviteMemberData,
  WorkspaceActivity,
  WorkspaceRole
} from '../types/workspace';
import { resolveInitialWorkspaceId } from '../utils/workspaceRoute';

interface WorkspaceContextType {
  activeUserId: string | undefined;
  // Current workspace state
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  workspaceMembers: WorkspaceMember[];
  workspaceActivities: WorkspaceActivity[];
  currentUserRole: WorkspaceRole | null;

  // Workspace management
  switchWorkspace: (workspaceId: string) => Promise<void>;
  createWorkspace: (workspaceData: CreateWorkspaceData) => Promise<string>;
  updateWorkspace: (workspaceId: string, updates: UpdateWorkspaceData) => Promise<void>;
  deleteWorkspace: (workspaceId: string) => Promise<void>;
  clearWorkspace: () => void;

  // Member management
  inviteMember: (inviteData: InviteMemberData) => Promise<void>;
  updateMemberRole: (memberId: string, role: WorkspaceRole) => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;

  // Data loading
  loadWorkspaces: () => Promise<void>;
  loadWorkspaceMembers: (workspaceId: string) => Promise<void>;
  loadWorkspaceActivities: (workspaceId: string) => Promise<void>;

  // State
  loading: boolean;
  isCurrentWorkspaceLocked: boolean;
  userPlan: string | null;
  error: string | null;

  // Shopify Embedded State
  isShopifyEmbedded: boolean;
  setIsShopifyEmbedded: (isEmbedded: boolean) => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log('📍 [TRACE] WorkspaceProvider: Rendering');
  const { currentUser } = useAuth();
  const { isAdmin, impersonatedUserId } = useAdmin();
  
  // Use impersonatedUserId if admin is impersonating, otherwise use current user
  const activeUserId = (isAdmin && impersonatedUserId) ? impersonatedUserId : currentUser?.uid;

  // State
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>([]);
  const [workspaceActivities, setWorkspaceActivities] = useState<WorkspaceActivity[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<WorkspaceRole | null>(null);
  const [loading, setLoading] = useState(false);
  const [isCurrentWorkspaceLocked, setIsCurrentWorkspaceLocked] = useState(false);
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isShopifyEmbedded, setIsShopifyEmbedded] = useState(false);

  // Load workspace members
  const loadWorkspaceMembers = useCallback(async (workspaceId: string) => {
    try {
      const members = await workspaceService.getWorkspaceMembers(workspaceId);
      setWorkspaceMembers(members);
    } catch (err) {
      console.error('Error loading workspace members:', err);
    }
  }, []);

  // Load workspace activities
  const loadWorkspaceActivities = useCallback(async (workspaceId: string) => {
    try {
      const activities = await workspaceService.getWorkspaceActivities(workspaceId, 50);
      setWorkspaceActivities(activities);
    } catch (err) {
      console.error('Error loading workspace activities:', err);
    }
  }, []);

  // Load user's workspaces
  const loadWorkspaces = useCallback(async () => {
    if (!activeUserId) {
      console.log('WorkspaceContext: No active user, skipping workspace load');
      return;
    }

    try {
      console.log('WorkspaceContext: Loading workspaces for user:', activeUserId);
      setLoading(true);
      setError(null);

      const userWorkspaces = await workspaceService.getUserWorkspaces(activeUserId);

      // Sort workspaces by createdAt ascending to ensure consistent "Primary" workspace
      userWorkspaces.sort((a, b) => {
        const dateA = a.createdAt instanceof Timestamp ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
        const dateB = b.createdAt instanceof Timestamp ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
        return dateA - dateB;
      });

      console.log('WorkspaceContext: Loaded and sorted workspaces:', userWorkspaces);
      console.log('WorkspaceContext: Number of workspaces found:', userWorkspaces.length);
      setWorkspaces(userWorkspaces);

      // Fetch user plan as well
      const { planService } = await import('../services/planService');
      const plan = await planService.getUserPlan(activeUserId);
      setUserPlan(plan);

      // Prefer workspace in the URL, then last selection, then oldest workspace
      const targetWorkspaceId = resolveInitialWorkspaceId(userWorkspaces);

      if (targetWorkspaceId) {
        // Fetch full workspace details
        const workspace = await workspaceService.getWorkspace(targetWorkspaceId);
        if (workspace) {
          const userRole = await workspaceService.getUserWorkspaceRole(targetWorkspaceId, activeUserId);
          if (userRole) {
            setCurrentWorkspace(workspace);
            setCurrentUserRole(userRole);
            await Promise.all([
              loadWorkspaceMembers(targetWorkspaceId),
              loadWorkspaceActivities(targetWorkspaceId)
            ]);
            localStorage.setItem('currentWorkspaceId', targetWorkspaceId);
          }
        }
      } else {
        // Clear workspace state if none are found
        setCurrentWorkspace(null);
        setCurrentUserRole(null);
        setWorkspaceMembers([]);
        setWorkspaceActivities([]);
        localStorage.removeItem('currentWorkspaceId');
      }
    } catch (err) {
      console.error('WorkspaceContext: Error loading workspaces:', err);
      setError('Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }, [activeUserId, loadWorkspaceMembers, loadWorkspaceActivities]);

  // Switch to a different workspace
  const switchWorkspace = useCallback(async (workspaceId: string) => {
    if (!activeUserId) return;

    try {
      setLoading(true);
      setError(null);

      // Get workspace details
      const workspace = await workspaceService.getWorkspace(workspaceId);
      if (!workspace) {
        throw new Error('Workspace not found');
      }

      // Get user's role in this workspace
      const userRole = await workspaceService.getUserWorkspaceRole(workspaceId, activeUserId);
      if (!userRole) {
        throw new Error('You are not a member of this workspace');
      }

      setCurrentWorkspace(workspace);
      setCurrentUserRole(userRole);

      // Load workspace-specific data
      await loadWorkspaceMembers(workspaceId);
      await loadWorkspaceActivities(workspaceId);

      // Store current workspace in localStorage
      localStorage.setItem('currentWorkspaceId', workspaceId);

    } catch (err) {
      console.error('Error switching workspace:', err);
      setError('Failed to switch workspace');
    } finally {
      setLoading(false);
    }
  }, [activeUserId, loadWorkspaceMembers, loadWorkspaceActivities]);

  // Create a new workspace
  const createWorkspace = useCallback(async (workspaceData: CreateWorkspaceData): Promise<string> => {
    if (!activeUserId) throw new Error('User not authenticated');

    try {
      setLoading(true);
      setError(null);

      // Pre-check workspace limits before attempting creation
      const { planService } = await import('../services/planService');
      const limitCheck = await planService.canCreateWorkspace(activeUserId);
      if (!limitCheck.allowed) {
        throw new Error(limitCheck.reason || 'Workspace limit reached. Please upgrade your plan.');
      }

      const workspaceId = await workspaceService.createWorkspace(activeUserId, workspaceData);

      // Get the created workspace details
      const newWorkspace = await workspaceService.getWorkspace(workspaceId);
      if (newWorkspace) {
        // Add to workspaces list
        setWorkspaces(prev => [...prev, newWorkspace]);

        // Switch to the new workspace
        await switchWorkspace(workspaceId);
      }

      return workspaceId;
    } catch (err) {
      console.error('Error creating workspace:', err);
      setError('Failed to create workspace');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [activeUserId, switchWorkspace]);

  // Update workspace
  const updateWorkspace = useCallback(async (workspaceId: string, updates: UpdateWorkspaceData) => {
    try {
      setLoading(true);
      setError(null);

      await workspaceService.updateWorkspace(workspaceId, updates);

      // Update local state
      if (currentWorkspace && currentWorkspace.id === workspaceId) {
        setCurrentWorkspace(prev => {
          if (!prev) return null;
          return {
            ...prev,
            ...updates,
            settings: {
              ...prev.settings,
              ...updates.settings
            }
          };
        });
      }

      // Update workspaces list
      setWorkspaces(prev => prev.map(w =>
        w.id === workspaceId ? { ...w, ...updates, settings: { ...w.settings, ...updates.settings } } : w
      ));

    } catch (err) {
      console.error('Error updating workspace:', err);
      setError('Failed to update workspace');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace]);

  // Delete workspace
  const deleteWorkspace = useCallback(async (workspaceId: string) => {
    try {
      setLoading(true);
      setError(null);

      await workspaceService.deleteWorkspace(workspaceId);

      // If this was the current workspace, switch to another one
      if (currentWorkspace && currentWorkspace.id === workspaceId) {
        const remainingWorkspaces = workspaces.filter(w => w.id !== workspaceId);
        if (remainingWorkspaces.length > 0) {
          await switchWorkspace(remainingWorkspaces[0].id);
        } else {
          setCurrentWorkspace(null);
          setCurrentUserRole(null);
          setWorkspaceMembers([]);
          setWorkspaceActivities([]);
        }
      }

      // Update workspaces list by filtering out the deleted workspace
      setWorkspaces(prev => prev.filter(w => w.id !== workspaceId));

      // Reload workspaces from the server to ensure consistency
      if (activeUserId) {
        await loadWorkspaces();
      }

    } catch (err) {
      console.error('Error deleting workspace:', err);
      setError('Failed to delete workspace');
    } finally {
      setLoading(false);
    }
  }, [activeUserId, currentWorkspace, workspaces, switchWorkspace, loadWorkspaces]);

  // Invite member
  const inviteMember = useCallback(async (inviteData: InviteMemberData) => {
    if (!currentWorkspace) throw new Error('No workspace selected');

    try {
      setLoading(true);
      setError(null);

      // TODO: Implement email invitation system
      // For now, we'll just log the activity
      await workspaceService.logWorkspaceActivity(
        currentWorkspace.id,
        activeUserId || '',
        'member_invited',
        `Invited ${inviteData.email} as ${inviteData.role}`,
        { email: inviteData.email, role: inviteData.role }
      );

      // Reload members
      await loadWorkspaceMembers(currentWorkspace.id);

    } catch (err) {
      console.error('Error inviting member:', err);
      setError('Failed to invite member');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, activeUserId, loadWorkspaceMembers]);

  // Update member role
  const updateMemberRole = useCallback(async (memberId: string, role: WorkspaceRole) => {
    try {
      setLoading(true);
      setError(null);

      await workspaceService.updateMemberRole(memberId, role);

      // Update local state
      setWorkspaceMembers(prev =>
        prev.map(member =>
          member.id === memberId ? { ...member, role, permissions: WORKSPACE_ROLE_PERMISSIONS[role] } : member
        )
      );

    } catch (err) {
      console.error('Error updating member role:', err);
      setError('Failed to update member role');
    } finally {
      setLoading(false);
    }
  }, []);

  // Remove member
  const removeMember = useCallback(async (memberId: string) => {
    try {
      setLoading(true);
      setError(null);

      await workspaceService.removeWorkspaceMember(memberId);

      // Update local state
      setWorkspaceMembers(prev => prev.filter(member => member.id !== memberId));

    } catch (err) {
      console.error('Error removing member:', err);
      setError('Failed to remove member');
    } finally {
      setLoading(false);
    }
  }, []);

  const clearWorkspace = useCallback(() => {
    setCurrentWorkspace(null);
    setCurrentUserRole(null);
    setWorkspaceMembers([]);
    setWorkspaceActivities([]);
    localStorage.removeItem('currentWorkspaceId');
  }, []);

  // Initialize workspace context when user logs in or impersonates someone
  useEffect(() => {
    if (activeUserId) {
      loadWorkspaces();
    } else {
      // Clear workspace state when user logs out or stops impersonating
      setCurrentWorkspace(null);
      setWorkspaces([]);
      setWorkspaceMembers([]);
      setWorkspaceActivities([]);
      setCurrentUserRole(null);
      localStorage.removeItem('currentWorkspaceId');
    }
  }, [activeUserId, loadWorkspaces]);

  // Calculate lock status whenever relevant data changes
  useEffect(() => {
    const checkLockStatus = async () => {
      if (!activeUserId || !currentWorkspace || workspaces.length === 0) {
        setIsCurrentWorkspaceLocked(false);
        return;
      }

      try {
        const { planService } = await import('../services/planService');
        const planId = await planService.getUserPlan(activeUserId);
        setUserPlan(planId);

        // Find index of current workspace in sorted list
        const index = workspaces.findIndex(w => w.id === currentWorkspace.id);

        if (index === -1) {
          setIsCurrentWorkspaceLocked(false);
          return;
        }

        // Check for workspace limits
        const features = await planService.getUserPlanFeatures(activeUserId);
        const maxWorkspaces = features.maxWorkspaces || 1;
        const isLocked = index >= maxWorkspaces;

        console.log(`[WorkspaceContext] Lock check for ${currentWorkspace.name}: index=${index}, max=${maxWorkspaces}, plan=${planId}, isLocked=${isLocked}`);
        setIsCurrentWorkspaceLocked(isLocked);
      } catch (error) {
        console.error('Error checking workspace lock status:', error);
        setIsCurrentWorkspaceLocked(false);
      }
    };

    checkLockStatus();
  }, [activeUserId, currentWorkspace, workspaces]);

  const value: WorkspaceContextType = useMemo(() => ({
    activeUserId,
    currentWorkspace,
    workspaces,
    workspaceMembers,
    workspaceActivities,
    currentUserRole,
    switchWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    clearWorkspace,
    inviteMember,
    updateMemberRole,
    removeMember,
    loadWorkspaces,
    loadWorkspaceMembers,
    loadWorkspaceActivities,
    loading,
    isCurrentWorkspaceLocked,
    userPlan,
    error,
    isShopifyEmbedded,
    setIsShopifyEmbedded,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [activeUserId, currentWorkspace, workspaces, workspaceMembers, workspaceActivities,
       currentUserRole, loading, isCurrentWorkspaceLocked, userPlan, error, isShopifyEmbedded]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
